import ApiService from './apiService';

export type DiaryStatus = 'draft' | 'submitted' | 'approved';
export type LeaveRequestType = 'availability' | 'leave';
export type LeaveRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export type SnagTaskStatus = 'todo' | 'in_progress' | 'done' | 'blocked';

export interface OperationsUser {
  id: string;
  name?: string;
  username?: string;
  email?: string;
}

export interface OperationsDepartment {
  id: string;
  name: string;
}

export interface DailySiteDiary {
  id: string;
  organizationId: string;
  departmentId?: string;
  createdById: string;
  diaryDate: string;
  weather?: string;
  manpowerSummary?: string;
  workCompleted?: string;
  delays?: string;
  incidents?: string;
  deliveries?: string;
  notes?: string;
  photoUrls?: string[];
  status: DiaryStatus;
  approvedAt?: string;
  createdAt: string;
  updatedAt: string;
  department?: OperationsDepartment;
  createdBy?: OperationsUser;
  approvedBy?: OperationsUser;
}

export interface LeaveRequest {
  id: string;
  organizationId: string;
  employeeId: string;
  departmentId?: string;
  type: LeaveRequestType;
  startDate: string;
  endDate: string;
  reason?: string;
  status: LeaveRequestStatus;
  reviewNote?: string;
  reviewedAt?: string;
  createdAt: string;
  employee?: OperationsUser;
  department?: OperationsDepartment;
  reviewedBy?: OperationsUser;
}

export interface SnagTask {
  id: string;
  organizationId: string;
  snagId: string;
  title: string;
  description?: string;
  assignedToId?: string;
  createdById: string;
  dueDate?: string;
  status: SnagTaskStatus;
  createdAt: string;
  updatedAt: string;
  snag?: { id: string; title: string; status?: string; priority?: string };
  assignedTo?: OperationsUser;
  createdBy?: OperationsUser;
}

export const OperationsService = {
  getDiaries: (params?: { startDate?: string; endDate?: string; departmentId?: string }) =>
    ApiService.get<DailySiteDiary[]>('/operations/diaries', { params }),

  createDiary: (data: Partial<DailySiteDiary>) =>
    ApiService.post<DailySiteDiary>('/operations/diaries', data),

  updateDiary: (id: string, data: Partial<DailySiteDiary>) =>
    ApiService.put<DailySiteDiary>(`/operations/diaries/${id}`, data),

  approveDiary: (id: string) =>
    ApiService.put<DailySiteDiary>(`/operations/diaries/${id}/approve`, {}),

  getLeaveRequests: (status?: LeaveRequestStatus) =>
    ApiService.get<LeaveRequest[]>('/operations/leave-requests', {
      params: status ? { status } : undefined,
    }),

  createLeaveRequest: (data: {
    type: LeaveRequestType;
    startDate: string;
    endDate: string;
    departmentId?: string;
    reason?: string;
  }) => ApiService.post<LeaveRequest>('/operations/leave-requests', data),

  approveLeaveRequest: (id: string, reviewNote?: string) =>
    ApiService.put<LeaveRequest>(`/operations/leave-requests/${id}/approve`, { reviewNote }),

  rejectLeaveRequest: (id: string, reviewNote?: string) =>
    ApiService.put<LeaveRequest>(`/operations/leave-requests/${id}/reject`, { reviewNote }),

  getSnagTasks: (params?: { snagId?: string; assignedToId?: string; status?: SnagTaskStatus }) =>
    ApiService.get<SnagTask[]>('/operations/snag-tasks', { params }),

  createSnagTask: (data: {
    snagId: string;
    title: string;
    description?: string;
    assignedToId?: string;
    dueDate?: string;
  }) => ApiService.post<SnagTask>('/operations/snag-tasks', data),

  updateSnagTask: (id: string, data: Partial<SnagTask>) =>
    ApiService.put<SnagTask>(`/operations/snag-tasks/${id}`, data),
};

export default OperationsService;
